import type { User } from "firebase/auth";
import { LogOut, Settings } from "lucide-react";
import React from "react";
import { useNavigate } from "react-router-dom";
import { UserAvatar } from "src/components/UserAvatar";
import { getRoleDisplayName, type UserProfile } from "src/lib/roles";
import type { ThemeKey } from "src/store/themeSlice";

type AppSidebarAccountMenuProps = {
  user: User;
  userProfile: UserProfile;
  layout: "rail" | "drawer";
  showLabels: boolean;
  accountMenuPanelId: string;
  accountMenuButtonId: string;
  currentTheme: ThemeKey;
  onThemeChange: (theme: ThemeKey) => void;
  onLogout: () => void;
};

const styles = {
  buttonHover: "hover:bg-[#EF4444]/20",
  buttonActive: "active:translate-y-0.5",
  buttonFocus:
    "focus:outline-none focus-visible:ring-4 focus-visible:ring-[#EF4444]/45",
  buttonRing: "ring-2 ring-[#14B8A6] ring-offset-2 ring-offset-gray-950",
  buttonRingOffset: "ring-offset-gray-950",
  buttonRingOffsetColor: "ring-offset-gray-950",
  buttonRingOffsetColorHover: "ring-offset-gray-950 hover:ring-offset-gray-950",
  buttonRingOffsetColorActive:
    "ring-offset-gray-950 active:ring-offset-gray-950",
};

export const AppSidebarAccountMenu: React.FC<AppSidebarAccountMenuProps> = ({
  user,
  userProfile,
  layout,
  showLabels,
  accountMenuPanelId,
  accountMenuButtonId,
  currentTheme,
  onLogout,
}) => {
  const logoutHoverByTheme: Record<ThemeKey, string> = {
    indigo: "hover:bg-indigo-500/20",
    emerald: "hover:bg-emerald-500/20",
    violet: "hover:bg-violet-500/20",
    rose: "hover:bg-rose-500/20",
    slate: "hover:bg-blue-500/20",
    liftoff: "hover:bg-indigo-500/20",
    midnight: "hover:bg-zinc-500/20",
  };

  const menuBgByTheme: Record<ThemeKey, string> = {
    indigo: "bg-indigo-950/90",
    emerald: "bg-emerald-950/90",
    violet: "bg-violet-950/90",
    rose: "bg-rose-950/90",
    slate: "bg-slate-900/90",
    liftoff: "bg-indigo-950/90",
    midnight: "bg-zinc-950/90",
  };

  const navigate = useNavigate();

  const menuItem = (
    icon: React.ReactNode,
    label: string,
    onClick: () => void,
  ) => (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={`flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl border-4 border-[#EF4444]/35 bg-[#EF4444]/12 px-3 py-2.5 text-sm font-extrabold text-red-100 shadow-[0_5px_0_#7F1D1D/60] transition-[transform,background-color,box-shadow] ${logoutHoverByTheme[currentTheme]} ${styles.buttonActive} ${styles.buttonFocus} ${styles.buttonRing} ${styles.buttonRingOffset} ${styles.buttonRingOffsetColor} ${styles.buttonRingOffsetColorHover} ${styles.buttonRingOffsetColorActive}`}
    >
      <div className="flex items-center justify-start w-full gap-6">
        {icon}
        {label}
      </div>
    </button>
  );

  return (
    <div
      id={accountMenuPanelId}
      role="menu"
      aria-labelledby={accountMenuButtonId}
      className={`absolute z-[100] rounded-2xl border-4 border-white/12 ${
        menuBgByTheme[currentTheme]
      } p-4 shadow-[0_10px_0_rgba(0,0,0,0.5)] backdrop-blur-xl ${
        layout === "drawer"
          ? "bottom-full left-0 right-0 mb-2 w-full"
          : showLabels
            ? "bottom-full left-0 right-0 mb-2 w-full"
            : "bottom-0 left-full ml-2 w-[min(18rem,calc(100vw-5rem))]"
      }`}
    >
      <div className="mb-4 flex gap-3 border-b-4 border-white/10 pb-4">
        <UserAvatar user={user} size="md" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-extrabold text-white">
            {user.displayName || user.email?.split("@")[0]}
          </p>
          <p className="truncate text-xs font-medium text-gray-500">
            {user.email}
          </p>
          <span className="mt-1.5 inline-block rounded-xl border-2 border-[#6366F1]/40 bg-[#6366F1]/15 px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wide text-indigo-100">
            {getRoleDisplayName(userProfile.role)}
          </span>
        </div>
      </div>

      {menuItem(
        <Settings className="h-4 w-4 shrink-0" aria-hidden />,
        "Settings",
        () => navigate("/settings"),
      )}

      {menuItem(
        <LogOut className="h-4 w-4 shrink-0" aria-hidden />,
        "Log out",
        onLogout,
      )}
    </div>
  );
};
